import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { inventoryAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Package, GitBranch, CheckCircle, XCircle, Clock, Loader2, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { ItemAttachmentsPreview } from '@/components/AttachmentThumb';

const TYPE_COLORS = {
  'Fixed Asset': 'bg-blue-50 text-blue-700 border-blue-200',
  'Raw Material': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'Service': 'bg-purple-50 text-purple-700 border-purple-200',
  'Consumable': 'bg-amber-50 text-amber-700 border-amber-200',
  'Semi-Finished Part': 'bg-cyan-50 text-cyan-700 border-cyan-200',
  'Finished Good': 'bg-teal-50 text-teal-700 border-teal-200',
  'Scrap': 'bg-slate-50 text-slate-600 border-slate-200',
  'Job Work Item': 'bg-orange-50 text-orange-700 border-orange-200',
};

export default function PendingApprovalsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [pendingItems, setPendingItems] = useState([]);
  const [pendingRoutes, setPendingRoutes] = useState([]);
  const [pendingChanges, setPendingChanges] = useState([]);
  const [activeTab, setActiveTab] = useState('items');
  
  // Approval/Rejection state
  const [approveTarget, setApproveTarget] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (user?.role !== 'admin') {
      toast.error('Access denied. Admin only.');
      navigate('/inventory');
      return;
    }
    loadPendingApprovals();
  }, [user]);

  const loadPendingApprovals = async () => {
    setLoading(true);
    try {
      const res = await inventoryAPI.getPendingApprovals();
      setPendingItems(res.data.pending_items || []);
      setPendingRoutes(res.data.pending_routes || []);
      setPendingChanges(res.data.pending_changes || []);
    } catch (err) {
      toast.error('Failed to load pending approvals');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!approveTarget) return;
    setActionLoading(true);
    try {
      if (approveTarget.type === 'item') {
        await inventoryAPI.approveItem(approveTarget.id);
        toast.success(`Item "${approveTarget.name}" approved`);
      } else if (approveTarget.type === 'change') {
        await inventoryAPI.approveChangeRequest(approveTarget.id);
        toast.success(`Change to "${approveTarget.name}" applied`);
      } else {
        await inventoryAPI.approveRoute(approveTarget.id);
        toast.success(`Route "${approveTarget.name}" approved`);
      }
      setApproveTarget(null);
      loadPendingApprovals();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectTarget) return;
    setActionLoading(true);
    try {
      if (rejectTarget.type === 'item') {
        await inventoryAPI.rejectItem(rejectTarget.id, rejectReason);
        toast.success(`Item "${rejectTarget.name}" rejected`);
      } else if (rejectTarget.type === 'change') {
        await inventoryAPI.rejectChangeRequest(rejectTarget.id, rejectReason);
        toast.success(`Change to "${rejectTarget.name}" rejected`);
      } else {
        await inventoryAPI.rejectRoute(rejectTarget.id, rejectReason);
        toast.success(`Route "${rejectTarget.name}" rejected`);
      }
      setRejectTarget(null);
      setRejectReason('');
      loadPendingApprovals();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject');
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', { 
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' 
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const totalPending = pendingItems.length + pendingRoutes.length + pendingChanges.length;

  return (
    <div className="space-y-4" data-testid="pending-approvals-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-xl font-bold tracking-tight flex items-center gap-2">
            <Clock className="w-5 h-5 text-amber-600" />
            Pending Approvals
          </h1>
          <p className="text-xs text-muted-foreground">
            {totalPending} item(s) awaiting your approval
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={loadPendingApprovals} data-testid="refresh-btn">
          Refresh
        </Button>
      </div>

      {totalPending === 0 && (
        <Card>
          <CardContent className="py-8 text-center">
            <CheckCircle className="w-10 h-10 mx-auto text-green-500 mb-2" />
            <p className="text-sm font-medium">All caught up!</p>
            <p className="text-xs text-muted-foreground">No pending approvals at this time.</p>
          </CardContent>
        </Card>
      )}
      {(
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="items" data-testid="tab-items">
              <Package className="w-3.5 h-3.5 mr-1" /> Items ({pendingItems.length})
            </TabsTrigger>
            <TabsTrigger value="routes" data-testid="tab-routes">
              <GitBranch className="w-3.5 h-3.5 mr-1" /> Routes/BOM ({pendingRoutes.length})
            </TabsTrigger>
            <TabsTrigger value="changes" data-testid="tab-changes">
              <Pencil className="w-3.5 h-3.5 mr-1" /> Changes ({pendingChanges.length})
            </TabsTrigger>
          </TabsList>

          {/* Items Tab */}
          <TabsContent value="items" className="mt-4">
            <Card>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="pending-items-table">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left p-2.5 text-xs font-medium text-muted-foreground">Unique ID</th>
                      <th className="text-left p-2.5 text-xs font-medium text-muted-foreground">Item Name</th>
                      <th className="text-left p-2.5 text-xs font-medium text-muted-foreground">Type</th>
                      <th className="text-left p-2.5 text-xs font-medium text-muted-foreground">Attachments</th>
                      <th className="text-left p-2.5 text-xs font-medium text-muted-foreground">Created By</th>
                      <th className="text-left p-2.5 text-xs font-medium text-muted-foreground">Created At</th>
                      <th className="text-left p-2.5 text-xs font-medium text-muted-foreground w-32">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingItems.length === 0 ? (
                      <tr><td colSpan={7} className="text-center py-8 text-xs text-muted-foreground">No pending items</td></tr>
                    ) : (
                      pendingItems.map(item => (
                        <tr key={item.id} className="border-b hover:bg-muted/50 transition-colors">
                          <td className="p-2.5 font-mono text-xs font-medium">
                            <span 
                              className="cursor-pointer hover:underline text-blue-600"
                              onClick={() => navigate(`/inventory/items/${item.id}`)}
                            >
                              {item.unique_id}
                            </span>
                          </td>
                          <td className="p-2.5 text-xs">{item.item_name}</td>
                          <td className="p-2.5">
                            <Badge variant="outline" className={`text-[10px] ${TYPE_COLORS[item.item_type] || ''}`}>
                              {item.item_type}
                            </Badge>
                          </td>
                          <td className="p-2.5">
                            <ItemAttachmentsPreview itemId={item.id} size="sm" max={4} />
                          </td>
                          <td className="p-2.5 text-xs text-muted-foreground">{item.created_by_name}</td>
                          <td className="p-2.5 text-xs text-muted-foreground">{formatDate(item.created_at)}</td>
                          <td className="p-2.5">
                            <div className="flex gap-1">
                              <Button 
                                size="sm" 
                                variant="default"
                                className="h-7 text-xs bg-green-600 hover:bg-green-700"
                                onClick={() => setApproveTarget({ type: 'item', id: item.id, name: item.unique_id })}
                                data-testid={`approve-item-${item.unique_id}`}
                              >
                                <CheckCircle className="w-3 h-3 mr-1" /> Approve
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="h-7 text-xs text-red-600 border-red-200 hover:bg-red-50"
                                onClick={() => setRejectTarget({ type: 'item', id: item.id, name: item.unique_id })}
                                data-testid={`reject-item-${item.unique_id}`}
                              >
                                <XCircle className="w-3 h-3 mr-1" /> Reject
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </TabsContent>

          {/* Routes Tab */}
          <TabsContent value="routes" className="mt-4">
            <Card>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="pending-routes-table">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left p-2.5 text-xs font-medium text-muted-foreground">Route Name</th>
                      <th className="text-left p-2.5 text-xs font-medium text-muted-foreground">For Item</th>
                      <th className="text-left p-2.5 text-xs font-medium text-muted-foreground">BOM Items</th>
                      <th className="text-left p-2.5 text-xs font-medium text-muted-foreground">Created By</th>
                      <th className="text-left p-2.5 text-xs font-medium text-muted-foreground">Created At</th>
                      <th className="text-left p-2.5 text-xs font-medium text-muted-foreground w-32">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingRoutes.length === 0 ? (
                      <tr><td colSpan={6} className="text-center py-8 text-xs text-muted-foreground">No pending routes</td></tr>
                    ) : (
                      pendingRoutes.map(route => (
                        <tr key={route.id} className="border-b hover:bg-muted/50 transition-colors">
                          <td className="p-2.5 text-xs font-medium">
                            <span 
                              className="cursor-pointer hover:underline text-blue-600"
                              onClick={() => navigate(`/inventory/routes/${route.id}`)}
                            >
                              {route.route_name}
                            </span>
                          </td>
                          <td className="p-2.5 text-xs">{route.item_name}</td>
                          <td className="p-2.5 text-xs text-muted-foreground">
                            {route.bill_of_materials?.length || 0} items
                          </td>
                          <td className="p-2.5 text-xs text-muted-foreground">{route.created_by_name}</td>
                          <td className="p-2.5 text-xs text-muted-foreground">{formatDate(route.created_at)}</td>
                          <td className="p-2.5">
                            <div className="flex gap-1">
                              <Button 
                                size="sm" 
                                variant="default"
                                className="h-7 text-xs bg-green-600 hover:bg-green-700"
                                onClick={() => setApproveTarget({ type: 'route', id: route.id, name: route.route_name })}
                                data-testid={`approve-route-${route.id}`}
                              >
                                <CheckCircle className="w-3 h-3 mr-1" /> Approve
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="h-7 text-xs text-red-600 border-red-200 hover:bg-red-50"
                                onClick={() => setRejectTarget({ type: 'route', id: route.id, name: route.route_name })}
                                data-testid={`reject-route-${route.id}`}
                              >
                                <XCircle className="w-3 h-3 mr-1" /> Reject
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </TabsContent>

          {/* Master change requests (non-admin edits of live items / BOMs) */}
          <TabsContent value="changes" className="mt-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Edits awaiting approval</CardTitle>
                <p className="text-xs text-muted-foreground">
                  Non-admin edits to live items and BOMs are held here. The record stays unchanged until you approve.
                </p>
              </CardHeader>
              <CardContent className="space-y-3" data-testid="pending-changes-list">
                {pendingChanges.length === 0 ? (
                  <p className="text-center py-8 text-xs text-muted-foreground">No pending changes</p>
                ) : pendingChanges.map(cr => (
                  <div key={cr.id} className="border rounded-lg p-3" data-testid={`change-request-${cr.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant="outline" className="text-[10px]">
                            {cr.entity_type === 'item' ? 'Item' : 'BOM/Route'}
                          </Badge>
                          <span
                            className="text-xs font-medium text-blue-600 cursor-pointer hover:underline"
                            onClick={() => navigate(cr.link || '/inventory')}
                          >
                            {cr.entity_label}
                          </span>
                        </div>
                        <p className="text-[11px] text-muted-foreground mt-0.5">
                          Requested by {cr.requested_by_name} on {formatDate(cr.requested_at)}
                        </p>
                        <ul className="mt-1.5 space-y-0.5">
                          {(cr.changes || []).map((c, ci) => (
                            <li key={ci} className="text-[11px] font-mono text-slate-700 break-all">{c}</li>
                          ))}
                        </ul>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <Button size="sm" className="h-7 text-xs bg-green-600 hover:bg-green-700"
                          onClick={() => setApproveTarget({ type: 'change', id: cr.id, name: cr.entity_label })}
                          data-testid={`approve-change-${cr.id}`}>
                          <CheckCircle className="w-3 h-3 mr-1" /> Approve
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 text-xs text-red-600 border-red-200 hover:bg-red-50"
                          onClick={() => setRejectTarget({ type: 'change', id: cr.id, name: cr.entity_label })}
                          data-testid={`reject-change-${cr.id}`}>
                          <XCircle className="w-3 h-3 mr-1" /> Reject
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}

      {/* Approve Confirmation Dialog */}
      <AlertDialog open={!!approveTarget} onOpenChange={() => setApproveTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-sm">
              <CheckCircle className="w-4 h-4 text-green-600" /> Approve {approveTarget?.type === 'item' ? 'Item' : approveTarget?.type === 'change' ? 'Change' : 'Route'}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-xs">
              Are you sure you want to approve <strong>{approveTarget?.name}</strong>?
              It will become active and available for use in operations.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="text-xs" disabled={actionLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleApprove} 
              className="text-xs bg-green-600 hover:bg-green-700"
              disabled={actionLoading}
              data-testid="confirm-approve"
            >
              {actionLoading ? 'Approving...' : 'Approve'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reject Dialog */}
      <AlertDialog open={!!rejectTarget} onOpenChange={() => { setRejectTarget(null); setRejectReason(''); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-sm">
              <XCircle className="w-4 h-4 text-red-600" /> Reject {rejectTarget?.type === 'item' ? 'Item' : rejectTarget?.type === 'change' ? 'Change' : 'Route'}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-xs">
              Rejecting <strong>{rejectTarget?.name}</strong>. Please provide a reason (optional):
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input
            placeholder="Reason for rejection..."
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            className="text-xs"
            data-testid="reject-reason-input"
          />
          <AlertDialogFooter>
            <AlertDialogCancel className="text-xs" disabled={actionLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleReject} 
              className="text-xs bg-red-600 hover:bg-red-700"
              disabled={actionLoading}
              data-testid="confirm-reject"
            >
              {actionLoading ? 'Rejecting...' : 'Reject'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
